import * as arcade from "@arcgis/core/arcade.js";
import type { Variable } from "@arcgis/core/arcade.js";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import * as bufferOperator from "@arcgis/core/geometry/operators/bufferOperator.js";
import * as containsOperator from "@arcgis/core/geometry/operators/containsOperator.js";
import * as projectOperator from "@arcgis/core/geometry/operators/projectOperator.js";
import Circle from "@arcgis/core/geometry/Circle";
import Point from "@arcgis/core/geometry/Point";
import Polygon from "@arcgis/core/geometry/Polygon";
import SpatialReference from "@arcgis/core/geometry/SpatialReference.js";
import * as webMercatorUtils from "@arcgis/core/geometry/support/webMercatorUtils.js";
import Graphic from "@arcgis/core/Graphic";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Layer from "@arcgis/core/layers/Layer";
import Field from "@arcgis/core/layers/support/Field";
import LabelClass from "@arcgis/core/layers/support/LabelClass";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleRenderer from "@arcgis/core/renderers/SimpleRenderer";
import UniqueValueRenderer from "@arcgis/core/renderers/UniqueValueRenderer";
import OpacityVariable from "@arcgis/core/renderers/visualVariables/OpacityVariable";
import esriRequest from "@arcgis/core/request.js";
import * as geoprocessor from "@arcgis/core/rest/geoprocessor.js";
import DataFile from "@arcgis/core/rest/support/DataFile.js";
import FeatureSet from "@arcgis/core/rest/support/FeatureSet";
import * as unionTypes from "@arcgis/core/unionTypes.js";
import MapView from "@arcgis/core/views/MapView";
import JSZip from "jszip";
import { toast } from "react-toastify";
import { AppConfig, CreateObjectConfig, RelatedLayerConfig } from "../AppConfig";
import { FeatureLayerNFS } from "../extensions/FeatureLayerNFS";
import { getKystverketRegion, getKystverketRode } from "./autofillUtils";
import { layerHasField, uuidv4, zoomToObject } from "./helpers";
import { NFSStatus } from "./nfsUtils";
import { FeatureClasses } from "../constants/NauticalObjects";
import { JaNei } from "../constants/nfsDomains";
import type PortalItem from "@arcgis/core/portal/PortalItem";
import type FeatureLayerView from "@arcgis/core/views/layers/FeatureLayerView";
import type { EditsResult, EditOptions, Edits } from "@arcgis/core/editing/types";
import type Relationship from "@arcgis/core/layers/support/Relationship";
import type CodedValueDomain from "@arcgis/core/layers/support/CodedValueDomain";
import type GraphicsLayerView from "@arcgis/core/views/layers/GraphicsLayerView";
import type { ResourceHandle } from "@arcgis/core/core/Handles";

export const GetCodedValueAndAlias = (layer: any, fieldName: string, fieldValue: number | string) => {
    let returnValue = [fieldName, fieldValue];
    if (!layer?.fields) return returnValue;
    layer.fields?.forEach((fld: any) => {
        if (fld.name === fieldName) {
            returnValue[0] = fld?.alias;
            const cDomain = fld?.domain;
            if (cDomain)
                cDomain.codedValues?.forEach((cVal: any) => {
                    if (cVal.code === fieldValue) returnValue[1] = cVal.name;
                });
        }
    });
    return returnValue;
};

export const getDomainCodeFromValue = (
    layer: FeatureLayer,
    fieldName: string,
    value: string
): string | number | undefined => {
    const domain = layer?.fields.find((field) => field.name === fieldName)?.domain as CodedValueDomain;
    const code = domain.codedValues.find((cv) => cv.name === value)?.code;
    return code;
};

export const IsFeatureInMapExtent = (feature: Graphic) => {
    const map = document.getElementsByTagName("arcgis-map")[0];
    if (!map || !feature.geometry) return false;

    const inExtent = containsOperator.execute(map.extent, feature.geometry);

    return inExtent;
};

export const getMapView = (id?: string) => {
    const maps = Array.from(document.getElementsByTagName("arcgis-map"));
    const map = maps.filter((map) => map.id === (id ?? "map"))[0];
    if (!map) {
        return;
    }
    const mapview = map.view;
    if (!mapview) {
        return;
    }
    return mapview;
};

export const highlightFeature = async (
    feature?: Graphic | null | undefined,
    highlightName: string = "default",
    layer?: Layer,
    mapViewId?: string
): Promise<ResourceHandle | void> => {
    const mapview = getMapView(mapViewId);
    if (!mapview || !feature?.layer) return;

    layer = layer ?? (feature?.layer instanceof Layer ? feature.layer : undefined);
    if (layer instanceof FeatureLayer || layer instanceof GraphicsLayer) {
        try {
            const result = await mapview.whenLayerView(layer);
            if (!result) return;
            return (result as FeatureLayerView | GraphicsLayerView).highlight(feature, {
                name: highlightName ?? "default",
            });
        } catch (error) {
            console.error("Error in highlightFeature:", error);
            return;
        }
    }
};

export const unHighlightGraphic = async (
    feature: Graphic | null | undefined,
    layer: GraphicsLayer,
    mapViewId?: string
) => {
    if (!feature || !layer) return;

    const mapview = getMapView(mapViewId);
    if (!mapview) return;

    const featureCopy = feature.clone();
    layer.remove(feature);
    layer.add(featureCopy);
};

export const setFeatureAttribute = (feature: Graphic, attributeName: string, attributeValue: any) => {
    const keys = Object.keys(feature.attributes);
    if (keys.includes(attributeName)) {
        feature.attributes[attributeName] = attributeValue;
    }
};

export const updateFeatureAttributes = async (updatedFeature: Graphic): Promise<boolean> => {
    const success = false;
    if (updatedFeature.geometry?.type === "point" && (updatedFeature.layer as FeatureLayer)?.hasZ) {
        updatedFeature.geometry.z = 0;
    }
    const result = await applyEdits(updatedFeature.layer as FeatureLayer, { updateFeatures: [updatedFeature] });

    if (result.updateFeatureResults[0].error) {
        console.error("Error updating feature: ", result.updateFeatureResults[0].error);
    } else {
        return true;
    }

    return success;
};

export const evaluateArcadeExpression = async (expression: string, feature: Graphic | null) => {
    if (!feature) return;

    const profile = {
        variables: [
            {
                name: "$feature",
                type: "feature",
            } as Variable,
        ],
    };

    const executor = await arcade.createArcadeExecutor(expression, profile);
    return executor.execute({ $feature: feature });
};

export const getDomainValues = (layer: FeatureLayer, fieldName: string) => {
    const domain = layer.getFieldDomain(fieldName) as CodedValueDomain;
    if (!domain) return [];
    return domain.codedValues;
};

export const canUseGeodesic = (mapview: MapView) => {
    return mapview.spatialReference.isWGS84 || mapview.spatialReference.isWebMercator;
};

export const projectToWebMercator = async (geometry: unionTypes.GeometryUnion) => {
    await projectOperator.load();
    if (!projectOperator.isLoaded) return geometry;
    return projectOperator.execute(geometry, new SpatialReference({ wkid: 3857 }));
};

export const projectToLatLong = async (geometry: unionTypes.GeometryUnion) => {
    await projectOperator.load();
    if (!projectOperator.isLoaded) return geometry;
    return projectOperator.execute(geometry, new SpatialReference({ wkid: 4326 }));
};

export const computeTrueNorth = async (point: Point | null | undefined): Promise<number> => {
    if (!point) return 0;

    const pointWebMercator = (await projectToWebMercator(point)) as Point;

    const lonLat = webMercatorUtils.xyToLngLat(pointWebMercator.x, pointWebMercator.y);

    const zeroMeridian = (AppConfig.Projection.CenterMeridian * Math.PI) / 180;
    const latitude = ((point.latitude ?? lonLat[1]) * Math.PI) / 180;
    const longitude = ((point.longitude ?? lonLat[0]) * Math.PI) / 180;

    const trueNorthAngle = Math.atan(Math.tan(zeroMeridian - longitude) * Math.sin(latitude));
    const trueNorthAngleDegrees = -(trueNorthAngle * 180) / Math.PI;
    return trueNorthAngleDegrees;
};

export const getNfsLayer = (layerId: number): FeatureLayer | null => {
    const mapView = document.getElementsByTagName("arcgis-map")[0];
    if (mapView?.map) {
        let allLayers = mapView.map.allLayers.toArray();
        allLayers = allLayers.concat(mapView.map.allTables.toArray());
        for (const l of allLayers) {
            if (l.type === "feature" && (l as FeatureLayerNFS)?.isVersioned) {
                if ((l as FeatureLayer).layerId === layerId) {
                    return l as FeatureLayer;
                }
            }
        }
    }
    return null;
};

/**
 * This should only be used for getting the names of NFS data layers.
 * It will not work for other layers.
 * @param layerId - The layerId of the layer to get the name from
 * @returns The name of the layer
 */
export const getLayerName = (layerId: number) => {
    const mapView = document.getElementsByTagName("arcgis-map")[0];
    let layerName: string | null | undefined = "";
    if (mapView?.map) {
        let allLayers = mapView.map.allLayers.toArray();
        allLayers = allLayers.concat(mapView.map.allTables.toArray());
        for (const l of allLayers) {
            if (l.type === "feature" && (l as FeatureLayerNFS)?.isVersioned) {
                if ((l as FeatureLayer).layerId === layerId) {
                    layerName = (l as FeatureLayer).title;
                }
            }
        }
    }
    return layerName;
};

/**
 * Returns the *versioned* FeatureLayer in the active map for a given layerId, or null
 * if no such layer exists. "Versioned" means the FeatureLayerNFS.isVersioned flag is
 * true — i.e. the layer is wired to the active gdbVersion by VersionManagementService.
 * Use this anywhere code needs to attach a graphic to a layer that should receive
 * writes through the version (and NOT directly into DEFAULT).
 */
export const getVersionedLayerById = (layerId: number | undefined | null): FeatureLayer | null => {
    if (layerId === undefined || layerId === null) return null;
    const mapView = document.getElementsByTagName("arcgis-map")[0];
    if (!mapView?.map) return null;
    const allLayers = mapView.map.allLayers.toArray().concat(mapView.map.allTables.toArray());
    const match = allLayers.find(
        (l) =>
            l.type === "feature" &&
            (l as FeatureLayerNFS).isVersioned === true &&
            (l as FeatureLayer).layerId === layerId
    );
    return (match as FeatureLayer) ?? null;
};

export const findAndSelectFeature = (
    layer: FeatureLayer,
    graphic: Graphic,
    selectFeature: (graphic: Graphic) => void
) => {
    const objectId = graphic.getObjectId();
    if (objectId === null || objectId === undefined) {
        console.error("Graphic does not have a valid objectId:", graphic);
        return;
    }
    // Dumb hack to get symbol change and highlight to work on new features
    layer
        .queryFeatures({
            objectIds: [objectId],
            outFields: ["*"],
            returnGeometry: true,
            geometryPrecision: 20,
        })
        .then((result) => {
            selectFeature(result.features[0]);
        });
};

export const getRelatedFeatures = async (features: Graphic[], relationshipName: string): Promise<Graphic[]> => {
    if (!features || features.length === 0) return [];

    // Group features by layer
    const featuresByLayer = features.reduce(
        (acc, feature) => {
            const layerId = (feature.layer as FeatureLayer)?.layerId;
            if (layerId === null || layerId === undefined) return acc;

            if (!acc[layerId]) {
                acc[layerId] = {
                    layer: feature.layer as FeatureLayer,
                    objectIds: [],
                };
            }

            const objectId = feature.getObjectId();
            if (objectId === null) {
                return acc;
            }

            if (objectId !== undefined && !acc[layerId].objectIds.includes(objectId)) {
                acc[layerId].objectIds.push(objectId);
            }

            return acc;
        },
        {} as Record<string, { layer: FeatureLayer; objectIds: (number | string)[] }>
    );

    // Process each layer group
    const allRelatedFeatures: Graphic[] = [];
    const mapview = getMapView();
    if (!mapview?.map) return allRelatedFeatures;
    const allTablesAndLayers = [...mapview.map.allLayers, ...mapview.map.allTables];

    for (const layerId in featuresByLayer) {
        const { layer, objectIds } = featuresByLayer[layerId];

        if (!layer || objectIds.length === 0) continue;

        const relationship = layer.relationships?.find((rel) => rel.name === relationshipName);
        if (relationship === undefined) continue;
        const relationshipId = relationship.id;

        const relationlayer = allTablesAndLayers.find(
            (l) => (l as FeatureLayer).layerId === relationship.relatedTableId
        ) as FeatureLayer;

        try {
            const result = await layer.queryRelatedFeatures({
                relationshipId,
                objectIds,
                outFields: ["*"],
                returnGeometry: true,
                geometryPrecision: 20,
            });

            // Collect all features from the results
            for (const objectId of objectIds) {
                const relatedFeatures = result[objectId]?.features || [];
                for (const f of relatedFeatures) {
                    f.layer = relationlayer;
                }
                allRelatedFeatures.push(...relatedFeatures);
            }
        } catch (error) {
            console.error(`Error querying related features for layer ${layerId}:`, error);
        }
    }

    return allRelatedFeatures;
};

export const getAllRelatedFeatures = async (
    feature: Graphic
): Promise<{ relationship: Relationship; features: Graphic[] }[]> => {
    const relatedFeatures: { relationship: Relationship; features: Graphic[] }[] = [];

    const layer = feature?.layer as FeatureLayer;
    if (!layer) return relatedFeatures;

    const relationships = layer.relationships;
    if (!relationships || relationships.length === 0) return relatedFeatures;

    if (!layer.loaded) {
        await layer.load();
    }

    const queries = relationships.map(async (relationship) => {
        let objectid = feature.getObjectId();
        if (objectid === null) {
            objectid = feature.attributes["objectid"];
        }
        if (objectid === null || objectid === undefined) {
            return { relationship, features: [] };
        }

        const result = await layer.queryRelatedFeatures({
            relationshipId: relationship.id,
            objectIds: [objectid],
            outFields: ["*"],
            where: "1=1",
            returnGeometry: true,
            geometryPrecision: 20,
        });

        const features = result[objectid]?.features || [];
        return { relationship, features };
    });

    const results = await Promise.all(queries);
    return results.filter((result) => result.features.length > 0);
};

export const getMainRelatedFeature = async (feature: Graphic) => {
    const mapview = getMapView();
    if (!mapview?.map) return feature;

    let layer = feature?.layer as FeatureLayer;
    if (!layer) return feature;

    let relationships = layer.relationships;
    if (!relationships || relationships.length === 0) return feature;

    const relationshipChain = AppConfig.Layers.find(
        (layerConfig) => layerConfig.Name === layer.id
    )?.RelationshipChainToMainFeature;

    if (!relationshipChain || relationshipChain.length === 0) return feature;

    let currentFeatureInChain: Graphic | undefined = feature;

    for (const relationshipNames of relationshipChain) {
        const relationship = relationships.find((relationship) => relationship.name === relationshipNames);
        if (!relationship) break;

        if (!currentFeatureInChain) break;
        const result = await layer.queryRelatedFeatures({
            relationshipId: relationship?.id,
            objectIds: [currentFeatureInChain.attributes.objectid],
            outFields: ["*"],
            returnGeometry: true,
            geometryPrecision: 20,
        });

        currentFeatureInChain = result[currentFeatureInChain.attributes.objectid]?.features[0];
        const allTablesAndLayers = [...mapview.map.allLayers, ...mapview.map.allTables];

        if (!currentFeatureInChain) break;
        layer = allTablesAndLayers.find(
            (l) => (l as FeatureLayer).layerId === relationship.relatedTableId
        ) as FeatureLayer;

        await layer.load();

        relationships = layer?.relationships ?? [];
        currentFeatureInChain.layer = layer;
    }

    return currentFeatureInChain;
};

export const isMainFeature = (feature: Graphic): boolean => {
    let isMainFeature = false;
    if (!feature) return isMainFeature;
    const mainFeatureLayersNames = AppConfig.MainObjectLayerNames;

    const layer = feature.layer as FeatureLayer;
    if (!layer) return isMainFeature;
    const layerName = layer.title?.replace("Nfs dev - ", "").replace("Nfs test - ", "");
    if (layerName) {
        isMainFeature = mainFeatureLayersNames.includes(layerName);
    }

    return isMainFeature;
};

export const isMainFeatureByLayerId = (layerId: number): boolean => {
    let isMainFeature = false;
    const mainFeatureLayerIds = AppConfig.MainObjectLayerIds;
    isMainFeature = mainFeatureLayerIds.includes(layerId);
    return isMainFeature;
};

/**
 * True for an IB/Flytendemerke that is paired with a Lys (the same flags FeatureField uses to
 * lock the attributes synced from the Lys).
 */
export const isPairedSecondaryFeature = (feature: Graphic): boolean => {
    const layerId = (feature.layer as FeatureLayer | undefined)?.layerId;
    const attributes = feature.attributes ?? {};
    if (layerId === FeatureClasses.IB) {
        return attributes.tilknyttet_hib == JaNei.Ja || attributes.tilknyttet_fl_la == JaNei.Ja;
    }
    if (layerId === FeatureClasses.Flytendemerke) {
        return attributes.tilknyttet_lysboye == JaNei.Ja;
    }
    return false;
};

export const getRelatedFeatureTree = async (feature: Graphic, visitedObjectIds: number[] = []) => {
    const allRelatedFeatures = [];
    const layer = feature?.layer as FeatureLayer;
    if (!layer) return [];

    const relationships = layer.relationships;
    if (!relationships || relationships.length === 0) return [];

    if (!layer.loaded) {
        await layer.load();
    }

    visitedObjectIds.push(feature.attributes.objectid);

    for (const relationship of relationships) {
        const result = await layer.queryRelatedFeatures({
            relationshipId: relationship.id,
            objectIds: [feature.attributes.objectid],
            outFields: ["*"],
            where: "1=1",
            returnGeometry: true,
            geometryPrecision: 20,
        });

        const features = result[feature.attributes.objectid]?.features || [];
        for (const f of features) {
            f.layer = feature.layer;
        }

        const filteredFeatures = features.filter((f) => !visitedObjectIds.includes(f.attributes.objectid));

        allRelatedFeatures.push(...filteredFeatures);

        for (const relatedFeature of filteredFeatures) {
            const childRelatedFeatures: Graphic[] = await getRelatedFeatureTree(relatedFeature, visitedObjectIds);
            allRelatedFeatures.push(...childRelatedFeatures);
        }
    }
    return allRelatedFeatures;
};

export const zoomTo = async (
    feature: Graphic,
    storeHighlightHandle?: (handle: ResourceHandle | null) => void,
    highlightName?: string
) => {
    if (!feature?.layer) {
        console.error("Feature or layer not found in search result.");
        return;
    }

    if (!feature.geometry) {
        console.error("Feature geometry not found in search result.");
        return;
    }

    const layer = feature.layer;
    let scale = 5000;
    const layerConfig = AppConfig.Layers.find((layerConfig) => layerConfig.Name === layer.id);
    if (layerConfig) {
        scale = layerConfig.zoomScale;
    }
    if ("minScale" in layer && "maxScale" in layer) {
        // @ts-expect-error minScale does not exist on generic layer type
        const minScale = feature.layer.minScale;
        // @ts-expect-error maxScale does not exist on generic layer type
        const maxScale = feature.layer.maxScale;
        if (scale > minScale) {
            scale = minScale;
        }
        if (scale < maxScale) {
            scale = maxScale;
        }
    }
    if (layer.visible === false) {
        layer.visible = true; // Ensure the layer is visible before zooming
    }
    const highlightHandle = await zoomToObject(feature, scale, undefined, true, highlightName);
    if (highlightHandle && storeHighlightHandle) {
        storeHighlightHandle(highlightHandle);
    }
};

export const getElevationOfFeature = async (feature: Graphic) => {
    if (!feature) return 0;
    const geometry = feature.geometry as Point;
    const mapView = getMapView();
    if (!mapView?.map) return 0;

    const result = await mapView.map.ground.queryElevation(geometry, { demResolution: "finest-contiguous" });
    return (result.geometry as Point).z;
};

export const zoomInClose = async (feature: Graphic) => {
    const mapview = getMapView();
    if (!mapview) return;
    if (!feature.geometry) {
        mapview.goTo({
            target: feature,
            scale: 100,
        });
    } else {
        const bufferedGeometry = bufferOperator.execute(feature.geometry, 10);
        mapview.goTo({
            target: bufferedGeometry ?? feature,
        });
    }
};

export const applyEdits = async (
    layer: FeatureLayer,
    edits: Edits,
    options?: EditOptions,
    setLoading?: (loading: boolean) => void
): Promise<EditsResult> => {
    if (!layer)
        return Promise.resolve({
            addFeatureResults: [],
            updateFeatureResults: [],
            deleteFeatureResults: [],
            addAttachmentResults: [],
            updateAttachmentResults: [],
            deleteAttachmentResults: [],
            editedFeatureResults: undefined,
            editMoment: undefined,
        } as EditsResult);

    const result = layer.applyEdits(edits, options);

    result.catch((error) => {
        toast.error(error.details?.messages.join("\n"));
        const allMessages = error.details?.messages.join("\n");
        if (allMessages.includes("Cannot acquire a lock")) {
            toast.info(
                "Det ser ut som versjonen har gått i lås. Dersom du er eier av versjonen kan du fjerne låsen i versjonsmenyen.",
                { autoClose: false }
            );
        }
        setLoading?.(false);
    });

    return result;
};

export const generateArcadeLabel = (enabledAttributes: string[]) => {
    let arcade = 'var label = "";';

    for (const element of enabledAttributes) {
        const attributeName = element;
        if (attributeName === "rekkevidde") {
            const rekkviddeAttributes = ["rekkevidde_hvit", "rekkevidde_rod", "rekkevidde_gronn", "rekkevidde_gul"];
            for (const rekkviddeAttribute of rekkviddeAttributes) {
                let color = rekkviddeAttribute.split("_")[1];
                color = color.replace("rod", "rød").replace("gronn", "grønn");
                arcade += `
                if (HasKey($feature, "${rekkviddeAttribute}")) {
                    var value = DomainName($feature, "${rekkviddeAttribute}");
                    if (value != null && value != "" && value != "Udefinert") {
                        if (label != "") {
                            label += " / ";
                        }
                        label += "(${color}) " + value + " Nm";
                    }
                }
                `;
            }
        } else if (attributeName === "lysstyrke") {
            const lysstyrkeAttributes = ["lysstyrke_hvit", "lysstyrke_rod", "lysstyrke_gronn", "lysstyrke_gul"];
            for (const lysstyrkeAttribute of lysstyrkeAttributes) {
                let color = lysstyrkeAttribute.split("_")[1];
                color = color.replace("rod", "rød").replace("gronn", "grønn");
                arcade += `
                if (HasKey($feature, "${lysstyrkeAttribute}")) {
                    var value = DomainName($feature, "${lysstyrkeAttribute}");
                    if (value != null && value != "" && value != "Udefinert") {
                        if (label != "") {
                            label += " / ";
                        }
                        label += "(${color}) " + value;
                    }
                }
                `;
            }
        } else if (attributeName === "toppmerke_retning" || attributeName === "retningslys_retning") {
            arcade += `
            if (HasKey($feature, "${attributeName}")) {
                var value = $feature.${attributeName};
                if (value != null && value != "" && value != "Udefinert") {
                    if (label != "") {
                        label += " / ";
                    }
                    label += value;
                }
            }
            `;
        } else {
            arcade += `
            if (HasKey($feature, "${attributeName}")) {
                var value = DomainName($feature, "${attributeName}");
                if (value != null && value != "" && value != "Udefinert") {
                    if (label != "") {
                        label += " / ";
                    }
                    label += value;
                }
            }
            `;
        }
    }

    arcade += `
    return label;`;

    return arcade;
};

export const applyLabelsToNFSLayers = async (enabledAttributes: string[]) => {
    const mapview = getMapView();
    if (!mapview?.map) return;

    const allLayers = [...mapview.map.allLayers];
    let nfsLayers = allLayers.filter((layer) => layer.type === "feature" && (layer as FeatureLayerNFS).isVersioned);
    nfsLayers = nfsLayers.filter((layer) => {
        // filter out layers with titles sektorlinje and sektorflate
        const layerTitle = (layer as FeatureLayer).title;
        return !layerTitle?.includes("Sektorlinje") && !layerTitle?.includes("Sektorflate");
    });

    for (const layer of nfsLayers) {
        const arcadeLabel = generateArcadeLabel(enabledAttributes);
        const arcadeLabelClass = new LabelClass({
            labelExpressionInfo: {
                expression: arcadeLabel,
            },
            labelPlacement: "above-center",
            symbol: {
                type: "text",
                color: [255, 255, 255, 0.7],
                haloColor: [0, 0, 0, 0.7],
                haloSize: 2,
                font: {
                    size: 14,
                    family: "sans-serif",
                },
            },
        });
        (layer as FeatureLayer).labelingInfo = [arcadeLabelClass];
    }
};

export const removeLabelsFromNFSLayers = async () => {
    const mapview = getMapView();
    if (!mapview?.map) return;

    const allLayers = [...mapview.map.allLayers];
    const nfsLayers = allLayers.filter((layer) => layer.type === "feature" && (layer as FeatureLayerNFS).isVersioned);

    for (const layer of nfsLayers) {
        (layer as FeatureLayer).labelingInfo = [];
    }
};

export const applyNFSStatusFilter = (visibleStatuses: number[], showRemovedFeatures: boolean) => {
    const mapview = getMapView();
    if (!mapview?.map) return;

    if (visibleStatuses.length === 0) {
        removeNFSStatusFilter(showRemovedFeatures);
        return;
    }

    const allLayers = [...mapview.map.allLayers];
    const nfsLayers = allLayers.filter(
        (layer) => layer.type === "feature" && (layer as FeatureLayerNFS).isVersioned
    ) as FeatureLayer[];

    const visibleStatusDefExp = "nfs_status in (" + visibleStatuses.join(",") + ")";

    for (const layer of nfsLayers) {
        if (layerHasField(layer, "nfs_status")) {
            layer.definitionExpression = visibleStatusDefExp;
        }
        if (layer.layerId === FeatureClasses.IB) {
            // As requested by Kystverket we hide IB that are linked to HIB
            layer.definitionExpression += " AND tilknyttet_hib = 0";
        }
        if (layer.layerId === FeatureClasses.Flytendemerke) {
            // As requested by Kystverket we hide Flytende merke that are linked to lysbøye
            layer.definitionExpression += " AND tilknyttet_lysboye = 0";
        }
    }
};

export const removeNFSStatusFilter = (showRemovedFeatures: boolean) => {
    const mapview = getMapView();
    if (!mapview?.map) return;

    const allLayers = [...mapview.map.allLayers];
    const nfsLayers = allLayers.filter(
        (layer) => layer.type === "feature" && (layer as FeatureLayerNFS).isVersioned
    ) as FeatureLayer[];
    for (const layer of nfsLayers) {
        if (layerHasField(layer, "nfs_status")) {
            layer.definitionExpression = removedAndDiscontinuedDefExpr(layer, showRemovedFeatures);
        }
        if (layer.layerId === FeatureClasses.IB) {
            // As requested by Kystverket we hide IB that are linked to HIB
            layer.definitionExpression += " AND tilknyttet_hib = 0";
        }
        if (layer.layerId === FeatureClasses.Flytendemerke) {
            // As requested by Kystverket we hide Flytende merke that are linked to lysbøye
            layer.definitionExpression += " AND tilknyttet_lysboye = 0";
        }
    }
};

export const indicateDiscontinuedOrRemovedFeatures = (view: MapView) => {
    view.map?.layers.forEach((layer) => {
        layer.load().then(() => {
            const featureLayer = layer as FeatureLayer;
            const renderer = featureLayer.renderer as UniqueValueRenderer | SimpleRenderer;

            if (!renderer) return;

            const statusField = AppConfig.Layers.find((configLayer) => configLayer.Name === layer.id)?.StatusField;

            const newVisualVariable = new OpacityVariable({
                valueExpression: `IIF(($feature.${statusField} == ${NFSStatus.DISCONTINUED}) || ($feature.${statusField} == ${NFSStatus.REMOVED}), ${AppConfig.DiscontinuedOpacity}, 1)`,
                stops: [
                    {
                        value: AppConfig.DiscontinuedOpacity,
                        opacity: AppConfig.DiscontinuedOpacity,
                    },
                    {
                        value: 1,
                        opacity: 1,
                    },
                ],
            });

            if (renderer.visualVariables) {
                renderer.visualVariables.push(newVisualVariable);
            } else {
                renderer.visualVariables = [newVisualVariable];
            }
        });
    });
};

export const exportFeatureToShape = async (feature: Graphic, fileName: string, _email: string) => {
    const featureSet = new FeatureSet({
        features: [feature],
    });

    try {
        const gpJob = await geoprocessor.submitJob(
            "https://maps.kystverket.cloudgis.no/enterprise/sharing/servers/e022df9acfea4b0aaacd2d6ff6626665/rest/services/GeoKonvertering/Geokonvertering/GPServer/Features2File",
            {
                Feature_set: featureSet,
                Ut_data_format: "shape",
                Ut_koordinat_system: "EPSG:25833",
                Ut_fil_navn: fileName,
                // Epost: email,
            }
        );
        toast.info(`Starter eksport av sjøkabel til shapefile`, { toastId: "export-shape-job" });

        const jobResult = await gpJob.waitForJobCompletion({
            interval: 1000,
            statusCallback: (status: any) => {
                toast.update("export-shape-job", {
                    render: `Eksporterer... Status: ${status.jobStatus}`,
                    isLoading: true,
                });
            },
        });
        if (jobResult.jobStatus === "job-succeeded") {
            toast.update("export-shape-job", {
                render: "Eksport til shape fullført, filen lastes nå ned.",
                type: "success",
                isLoading: false,
                autoClose: 5000,
            });
            // Extract the download link from the job result
            const downloadLink = await jobResult.fetchResultData("Link");
            open(downloadLink.value as string, "_blank", "noopener,noreferrer");
        }
    } catch (error) {
        console.error("Error submitting geoprocessing job:", error);
        toast.error("Det oppstod en feil under eksport av shapefilen.");
    }
};

export const getFeatureFromGlobalId = (layerId: number, globalId: string): Promise<Graphic | null> => {
    const mapView = getMapView();
    if (!mapView?.map) return Promise.resolve(null);

    const layer = mapView.map.layers.find(
        (layer) => layer.type === "feature" && (layer as FeatureLayer).layerId === layerId
    ) as FeatureLayer;

    return layer
        .queryFeatures({
            where: `globalid = '${globalId}'`,
            returnGeometry: true,
            outFields: ["*"],
        })
        .then((result) => {
            if (result.features.length > 0) {
                return result.features[0];
            }
            return null;
        });
};

export const getFeaturesFromGlobalIds = async (
    requests: { layerId: number; globalIds: string[] }[]
): Promise<{ layerId: number; features: Graphic[] }[]> => {
    const mapView = getMapView();
    if (!mapView?.map) return [];

    const results: { layerId: number; features: Graphic[] }[] = [];

    for (const request of requests) {
        const { layerId, globalIds } = request;

        if (!globalIds.length) continue;
        const allLayers = [...mapView.map.allLayers, ...mapView.map.allTables];
        const layer = allLayers.find(
            (layer) => layer.type === "feature" && (layer as FeatureLayer).layerId === layerId
        ) as FeatureLayer;

        if (!layer) continue;

        try {
            // Use IN operator for better performance with multiple IDs
            const globalIdList = globalIds.map((id) => `'${id}'`).join(",");
            const queryResult = await layer.queryFeatures({
                where: `globalid IN (${globalIdList})`,
                returnGeometry: true,
                outFields: ["*"],
            });

            results.push({
                layerId,
                features: queryResult.features,
            });
        } catch (error) {
            console.error(`Error querying features for layerId ${layerId}:`, error);
            results.push({ layerId, features: [] });
        }
    }

    return results;
};

export const convertFilesToSosi = async (files: File[]) => {
    const zipper = new JSZip();
    for (const file of files) {
        zipper.file(file.name, file);
    }
    //Manual test
    // convertSosiToFeatures("");
    const zippedFiles = await zipper.generateAsync({ type: "blob" });
    const sosiZipBlob = await geoprocessFilesToSosi(zippedFiles);
    if (!sosiZipBlob) {
        return;
    }

    // let itemId = await uploadFileToGPServer(sosiZipBlob);
    // itemId = itemId.item.itemID;
    // const _features = await convertSosiToFeatures(sosiZipBlob);
};

export interface ImportFileWithSrid {
    file: File;
    srid?: number;
}

const geoprocessFilesToSosi = async (zippedFiles: Blob) => {
    const uploadResult = await uploadFileToGPServer(
        zippedFiles,
        "https://maps.kystverket.cloudgis.no/enterprise/sharing/servers/e022df9acfea4b0aaacd2d6ff6626665/rest/services/GeoKonvertering/Geokonvertering/GPServer/uploads/upload"
    );
    const zippedDatafile = new DataFile({
        itemId: uploadResult.item.itemID,
    });
    try {
        const gpJob = await geoprocessor.submitJob(
            "https://maps.kystverket.cloudgis.no/enterprise/sharing/servers/e022df9acfea4b0aaacd2d6ff6626665/rest/services/GeoKonvertering/Geokonvertering/GPServer/File2SOSI",
            {
                ZIP_FILE: zippedDatafile,
                Ut_koordinat_system: "EPSG:25833",
                // Ut_fil_navn: fileName,
                // Epost: email,
            }
        );
        toast.info(`Starter konvertering av filer til SOSI`, { toastId: "convert-to-sosi-job" });

        const jobResult = await gpJob.waitForJobCompletion({
            interval: 1000,
            statusCallback: (status: any) => {
                toast.update("convert-to-sosi-job", {
                    render: `Konverterer... Status: ${status.jobStatus}`,
                    isLoading: true,
                });
            },
        });
        if (jobResult.jobStatus === "job-succeeded") {
            toast.update("convert-to-sosi-job", {
                render: "Konvertering til SOSI fullført, filen lastes nå ned.",
                type: "success",
                isLoading: false,
                autoClose: 5000,
            });
            // Extract the download link from the job result
            const downloadLink = await jobResult.fetchResultData("Link");
            const sosiZipBlob = (await fetch(downloadLink.value as string)).blob();
            return sosiZipBlob;
        }
    } catch (error) {
        console.error("Error submitting geoprocessing job:", error);
        toast.error("Det oppstod en feil under eksport av shapefilen.");
    }
};

const uploadFileToGPServer = async (file: Blob | File, url: string, filename?: string) => {
    // const token = await getAGEToken();
    try {
        const formData = new FormData();
        formData.append("file", new Blob([file], { type: file.type }), filename ?? "upload.zip");
        formData.append("description", "File uploaded for geoprocessing");
        formData.append("f", "json");

        const response = await esriRequest(url, {
            method: "post",
            body: formData,
            withCredentials: true,
        });
        return response.data;
    } catch (error) {
        console.error("Error submitting geoprocessing job:", error);
        toast.error("Det oppstod en feil under opplasting av filen.");
    }
};

// @ts-ignore Might be usefull for future reference
const convertSosiToFeatures = async (sosiZipBlob: Blob) => {
    const uploadResult = await uploadFileToGPServer(
        sosiZipBlob,
        "https://maps.kystverket.cloudgis.no/enterprise/sharing/servers/e022df9acfea4b0aaacd2d6ff6626665/rest/services/GeoKonvertering/Geokonvertering/GPServer/uploads/upload"
    );
    const sosiDatafile = new DataFile({
        itemId: uploadResult.item.itemID,
    });
    try {
        const gpJob = await geoprocessor.submitJob(
            "https://maps.kystverket.cloudgis.no/enterprise/sharing/servers/e022df9acfea4b0aaacd2d6ff6626665/rest/services/GeoKonvertering/Geokonvertering/GPServer/SOSI2Features",
            {
                SOSI: sosiDatafile,
                Ut_koordinat_system: "EPSG:25833",
                // Ut_fil_navn: fileName,
                // Epost: email,
            }
        );
        toast.info(`Starter konvertering av SOSI til Features`, { toastId: "convert-to-features-job" });

        const jobResult = await gpJob.waitForJobCompletion({
            interval: 1000,
            statusCallback: (status: any) => {
                toast.update("convert-to-features-job", {
                    render: `Konverterer... Status: ${status.jobStatus}`,
                    isLoading: true,
                });
            },
        });
        if (jobResult.jobStatus === "job-succeeded") {
            toast.update("convert-to-features-job", {
                render: "Konvertering til Features fullført, filen lastes nå ned.",
                type: "success",
                isLoading: false,
                autoClose: 5000,
            });

            const pointFeatures = await jobResult.fetchResultData("Features_Punkt");
            const lineFeatures = await jobResult.fetchResultData("Features_Linje");
            const polygonFeatures = await jobResult.fetchResultData("Features_Polygon");
            return {
                pointFeatures,
                lineFeatures,
                polygonFeatures,
            };
            // Extract the download link from the job result
            // const downloadLink = await jobResult.fetchResultData("Link");
            // download sosi file a blob
            // const sosiBlob = await fetch(downloadLink.value as string).then(res => res.blob());
            // saveAs(sosiBlob, "fil.zip");
            // return downloadLink.value as string;
        }
    } catch (error) {
        console.error("Error submitting geoprocessing job:", error);
        toast.error("Det oppstod en feil under konvertering av filene.");
    }
};

const checkIfRequiredFilesForShapefileExist = (files: File[] | ImportFileWithSrid[]) => {
    const requiredExtensions = [".shp", ".shx", ".dbf", ".prj"];
    const fileExtensions = new Set(
        files.map((fileOrEntry) => {
            const file = fileOrEntry instanceof File ? fileOrEntry : fileOrEntry.file;
            return file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
        })
    );
    return requiredExtensions.every((ext) => fileExtensions.has(ext));
};

/**
 * Using the portal content/features/generate rest endpoint
 * Accepts filetypes CSV, GeoJSON, Shapefile and GPX
 * @param files
 */
export const generateFeaturesFromFiles = async (files: File[] | ImportFileWithSrid[]) => {
    const importFiles: ImportFileWithSrid[] = files.map((fileOrEntry) => {
        if (fileOrEntry instanceof File) {
            return { file: fileOrEntry };
        }
        return fileOrEntry;
    });

    // Can only process one file or file bundle at once.
    // Separate files by name without extension
    // Create a dictionary of file array where files are stored by name
    const fileGroups = importFiles.reduce(
        (acc, fileEntry) => {
            const file = fileEntry.file;
            // Handle dual extensions by checking for patterns like .shp.xml
            const fileName = file.name.replace(/(\.[^/.]+)*$/, "");
            if (!acc[fileName]) {
                acc[fileName] = [];
            }
            acc[fileName].push(fileEntry);
            return acc;
        },
        {} as Record<string, ImportFileWithSrid[]>
    );

    const portalResponses: { name: string; response: any }[] = [];

    for (const groupKey in fileGroups) {
        const fileGroup = fileGroups[groupKey];
        const zipper = new JSZip();
        let fileType = "";
        for (const fileEntry of fileGroup) {
            const file = fileEntry.file;
            zipper.file(file.name, file);
            if (fileType === "") {
                if (file.name.endsWith(".shp")) {
                    fileType = "shapefile";
                } else if (file.name.endsWith(".json") || file.name.endsWith(".geojson")) {
                    fileType = "geojson";
                } else if (file.name.endsWith(".gpx")) {
                    fileType = "gpx";
                } else if (file.name.endsWith(".csv")) {
                    fileType = ""; // not supporting for now
                } else if (file.name.endsWith(".dwg")) {
                    fileType = "dwg";
                } else if (file.name.endsWith(".dxf")) {
                    fileType = "dxf";
                }
            }
        }

        if (fileType === "") {
            console.error("Unsupported file type for: " + groupKey);
            toast.error("Filen har et format som ikke støttes: " + groupKey + " (" + fileGroup[0].file.type + ")");
            continue;
        }

        if (fileType === "shapefile" && !checkIfRequiredFilesForShapefileExist(fileGroup)) {
            toast.error("Shapefile mangler en eller flere nødvendige filer (.shp, .shx, .dbf, .prj): " + groupKey);
            continue;
        }

        if (fileType === "dwg" || fileType === "dxf") {
            try {
                if (fileGroup.length === 1) {
                    const fileNameWithExtension = fileGroup[0].file.name;
                    await convertDWGToLayer(fileGroup[0].file, fileNameWithExtension, fileGroup[0].srid ?? 32633);
                } else {
                    const cadFiles = fileGroup.filter(
                        (entry) => entry.file.name.endsWith(".dwg") || entry.file.name.endsWith(".dxf")
                    );
                    if (cadFiles.length === 0) {
                        toast.error("Ingen DWG eller DXF filer funnet i gruppen: " + groupKey);
                        continue;
                    }
                    for (const cadFile of cadFiles) {
                        const fileNameWithExtension = cadFile.file.name;
                        await convertDWGToLayer(cadFile.file, fileNameWithExtension, cadFile.srid ?? 32633);
                    }
                }
                continue;
            } catch (error) {
                console.error("Error converting DWG/DXF file:", error);
                toast.error("Det oppsto en feil ved konvertering av DWG/DXF filen: " + groupKey);
                continue;
            }
        }

        const mapview = getMapView();

        const zipBlob = await zipper.generateAsync({ type: "blob" });
        const params = {
            name: groupKey,
            targetSR: mapview?.spatialReference ?? { wkid: 25833 },
            //   maxRecordCount: 1000,
            // enforceInputFileSizeLimit: true,
            // enforceOutputJsonSizeLimit: true,
        };
        if (fileType !== "dwg/dxf") {
            const formdata = new FormData();
            formdata.append("filetype", fileType);
            formdata.append("f", "json");
            formdata.append("publishParameters", JSON.stringify(params));
            if (fileType === "geojson" && fileGroup.length === 1) {
                formdata.append("file", fileGroup[0].file, groupKey + ".json");
            } else if (fileType === "shapefile") {
                formdata.append("file", zipBlob, groupKey + ".zip");
            } else if (fileType === "csv" && fileGroup.length === 1) {
                formdata.append("file", fileGroup[0].file, groupKey + ".csv");
            } else if (fileType === "gpx" && fileGroup.length === 1) {
                formdata.append("file", fileGroup[0].file, groupKey + ".gpx");
            } else if (fileGroup.length === 1) {
                formdata.append("file", fileGroup[0].file);
            } else {
                formdata.append("file", zipBlob, groupKey + ".zip");
            }
            const toastid = toast.info("Prosesserer: " + groupKey + ". Dette kan ta litt tid.", { isLoading: true });
            try {
                const response = await esriRequest(
                    "https://maps.kystverket.cloudgis.no/enterprise/sharing/rest/content/features/generate",
                    {
                        body: formdata,
                    }
                );
                toast.dismiss(toastid);
                portalResponses.push({ name: groupKey, response });
            } catch (error) {
                console.error("Error processing file:", error);
                toast.error("Det oppsto en feil ved behandling av filen: " + groupKey);
            }
        }
    }
    for (const res of portalResponses) {
        if (res.response.data?.error) {
            toast.error("Feil ved opplasting: " + res.response.data.error.message);
        } else if (res.response.data?.featureCollection) {
            addFeatureCollectionToMap(res.response.data.featureCollection, res.name);
            toast.success("Filen er lastet opp og lagt til i kartet.");
        }
    }
};

const addFeatureCollectionToMap = (featureCollection: any, name: string) => {
    let sourceGraphics: Graphic[] = [];
    const mapview = getMapView();
    if (!mapview?.map) return;

    const layers = featureCollection.layers.map((layer: any) => {
        const graphics = layer.featureSet.features.map((feature: any) => {
            return Graphic.fromJSON(feature);
        });
        sourceGraphics = sourceGraphics.concat(graphics);
        const featureLayer = new FeatureLayer({
            objectIdField: "FID",
            source: graphics,
            popupEnabled: true,
            title: "Import_" + name, // + "_" + layer.layerDefinition.name,
            fields: layer.layerDefinition.fields.map((field: any) => {
                return Field.fromJSON(field);
            }),
        });
        return featureLayer;
    });
    mapview.map.addMany(layers);
    mapview.goTo(sourceGraphics).catch((error) => {
        if (error.name != "AbortError") {
            console.error(error);
        }
    });
};

export const removedAndDiscontinuedDefExpr = (layer: FeatureLayer, showFeatures: boolean) => {
    const statusField = AppConfig.Layers.find((configLayer) => configLayer.Name === layer.id)?.StatusField;
    if (!statusField || showFeatures) return "1=1";

    const allowedStatuses = Object.values(NFSStatus)
        .filter((status): status is NFSStatus => typeof status === "number")
        .filter((status) => status !== NFSStatus.REMOVED && status !== NFSStatus.DISCONTINUED);

    return `${statusField} IN (${allowedStatuses.join(", ")})`;
};

const addFeatureAndSetIds = async (
    featureLayer: FeatureLayer,
    feature: Graphic,
    region: number,
    rode: string,
    relatedLayerConfig: RelatedLayerConfig | CreateObjectConfig | null | undefined
) => {
    const mapView = getMapView();
    if (!mapView?.map) return;

    const dummyFeature = new Graphic({
        attributes: featureLayer?.fields
            ?.map((field) => field.name)
            .reduce((acc, field) => ({ ...acc, [field]: null }), {}),
        layer: featureLayer,
    });

    for (const presetAttribute of relatedLayerConfig?.FieldValues ?? []) {
        setFeatureAttribute(dummyFeature, presetAttribute.FieldName, presetAttribute.Value);
    }

    if (dummyFeature !== null) {
        feature.attributes = { ...dummyFeature.attributes, ...feature.attributes };
        feature.layer = featureLayer;
        // Only overwrite with a value the lookup actually resolved. createRelatedFeatures
        // passes 0 / "" when the region/rode query fails, and writing those would wipe
        // values a caller (the replace flow) already copied onto the feature.
        if (region) {
            setFeatureAttribute(feature, "kystverk_region", region);
        }
        if (rode) {
            setFeatureAttribute(feature, "kystverk_rode", rode);
        }
    }
    if (featureLayer.hasZ && feature.geometry?.type === "point") {
        feature.geometry.z = 0;
    }

    const editsResult = await applyEdits(
        featureLayer,
        {
            addFeatures: [feature],
        },
        { globalIdUsed: true }
    );

    // We assume that only one related feature is created per layer
    feature.attributes.objectid = editsResult.addFeatureResults[0].objectId;

    await applyEdits(
        featureLayer,
        {
            updateFeatures: [feature],
        },
        { globalIdUsed: true }
    );

    return feature;
};

export const createRelatedFeatures = async (
    graphic: Graphic,
    layer: FeatureLayer,
    selectedLayerConfig: CreateObjectConfig | RelatedLayerConfig | null | undefined,
    userName: string | null | undefined,
    createTopLevelFeature: boolean = true
) => {
    if (!selectedLayerConfig) return;

    const mapView = getMapView();
    if (!mapView?.map) return;

    const relationshipTableEntries = [];
    const relationshipsMainObject = layer.relationships;

    if (createTopLevelFeature) {
        let region = 0;
        let rode = "";
        try {
            region = await getKystverketRegion(graphic);
            rode = await getKystverketRode(graphic);
        } catch (error) {
            console.error("Error getting region/rode for feature:", error);
        }
        await addFeatureAndSetIds(layer, graphic, region, rode, selectedLayerConfig);
    }

    for (const relatedLayerEntry of selectedLayerConfig.RelatedLayers ?? []) {
        let relatedLayer: FeatureLayer;

        const layerInfo = AppConfig.Layers.find((l) => l.Name === relatedLayerEntry.LayerName);

        if (layerInfo?.IsTable) {
            relatedLayer = mapView.map?.findTableById(relatedLayerEntry.LayerName) as FeatureLayer;
        } else {
            relatedLayer = mapView.map?.findLayerById(relatedLayerEntry.LayerName) as FeatureLayer;
        }
        if (!relatedLayer) continue;

        // Create new feature
        const relatedFeature = new Graphic({
            geometry: layerInfo?.IsTable ? null : graphic.geometry,
            attributes: {
                globalid: uuidv4(),
            },
            layer: relatedLayer,
        });

        // Find the name of the attribute in the related object that relates to the main object.
        // This attribute will get the value from the main object's key field.
        const relationshipMainObject = relationshipsMainObject?.find((r) => r.relatedTableId === relatedLayer.layerId);
        if (!relationshipMainObject) continue;

        if (
            relationshipMainObject.relationshipTableId !== null &&
            relationshipMainObject.relationshipTableId !== undefined
        ) {
            const keyFieldMainObject = relationshipMainObject.keyField;
            const relationshipTableId = relationshipMainObject.relationshipTableId;
            const relationshipTable = mapView.map?.tables.find(
                (layer) => (layer as FeatureLayer).layerId === relationshipTableId
            );
            if (!relationshipTable) continue;

            const relationshipsRelatedObject = relatedLayer.relationships;
            const relationshipRelatedObject = relationshipsRelatedObject?.find((r) => r.name === layer.id);
            if (!relationshipRelatedObject) continue;

            const keyFieldRelatedObject = relationshipRelatedObject.keyField;
            relationshipTableEntries.push({
                layer: relationshipTable as FeatureLayer,
                mainObject: graphic,
                relatedObject: relatedFeature,
                keyFieldMainObject: keyFieldMainObject,
                keyFieldMainObjectInTable: relationshipMainObject.keyFieldInRelationshipTable,
                keyFieldRelatedObject: keyFieldRelatedObject,
                keyFieldRelatedObjectInTable: relationshipRelatedObject.keyFieldInRelationshipTable,
                mainFeatureGlobalId: graphic.attributes.globalid,
            });

            for (const field of relatedLayerEntry.FieldValues ?? []) {
                relatedFeature.attributes[field.FieldName] = field.Value;
            }
        } else {
            const keyFieldAliasMainObject = relationshipMainObject.keyField;
            const keyFieldMainObject = layer.fields.find((f) => f.alias === keyFieldAliasMainObject);
            // Try finding by name if alias search fails
            if (!keyFieldMainObject) continue;

            const keyFieldName = keyFieldMainObject?.name;

            // Now find the name of the attribute in the main object that we will get the value from.
            const relationshipsRelatedObject = relatedLayer.relationships;
            const relationshipRelatedObject = relationshipsRelatedObject?.find(
                (r) => r.relatedTableId === layer.layerId
            );
            if (!relationshipRelatedObject) continue;

            const keyFieldAliasRelatedObject = relationshipRelatedObject.keyField;
            const keyFieldRelatedObject = relatedLayer.fields.find((f) => f.alias === keyFieldAliasRelatedObject);
            // Try finding by name if alias search fails
            if (!keyFieldRelatedObject) continue;

            const keyFieldNameRelated = keyFieldRelatedObject?.name;

            for (const field of relatedLayerEntry.FieldValues ?? []) {
                relatedFeature.attributes[field.FieldName] = field.Value;
            }
            // Establish relationship <3
            relatedFeature.attributes[keyFieldNameRelated] = graphic.attributes[keyFieldName];
        }

        await createRelatedFeatures(relatedFeature, relatedLayer, relatedLayerEntry, userName, true);
    }

    for (const relationshipTableEntry of relationshipTableEntries) {
        const newRelationshipTableData = new Graphic({
            attributes: {},
            layer: relationshipTableEntry.layer,
        });

        // Establish a relationship <3
        newRelationshipTableData.attributes[relationshipTableEntry.keyFieldMainObjectInTable] =
            relationshipTableEntry.mainObject.attributes[relationshipTableEntry.keyFieldRelatedObject];
        newRelationshipTableData.attributes[relationshipTableEntry.keyFieldRelatedObjectInTable] =
            relationshipTableEntry.relatedObject.attributes[relationshipTableEntry.keyFieldRelatedObject];
        await applyEdits(relationshipTableEntry?.layer, {
            addFeatures: [newRelationshipTableData],
        });
    }
};

const addGDOFeaturesToMap = (GDOfeatures: any, name: string) => {
    let sourceGraphics: Graphic[] = [];
    const mapview = getMapView();
    if (!mapview?.map) return;

    const graphics = GDOfeatures.features;

    if (graphics.length === 0) {
        return;
    }

    sourceGraphics = sourceGraphics.concat(graphics);
    const featureLayer = new FeatureLayer({
        objectIdField: "OBJECTID",
        source: graphics,
        popupEnabled: true,
        title: "Import_" + name, // + "_" + layer.layerDefinition.name,
        fields: GDOfeatures.fields,
    });

    mapview.map.add(featureLayer);
    mapview.goTo(sourceGraphics).catch((error) => {
        if (error.name != "AbortError") {
            console.error(error);
        }
    });
};

/**
 *
 * @param dwgFile - A DWG, DXF file or zip collection as a Blob
 * @returns
 */
export const convertDWGToFeatures = async (dwgFile: Blob, fileName: string) => {
    const uploadResult = await uploadFileToGPServer(
        dwgFile,
        "https://maps.kystverket.cloudgis.no/enterprise/sharing/servers/c0d3e56531604bac9d129f64a90450c5/rest/services/GeoKonvertering/GeokonverteringUtvidet/GPServer/uploads/upload"
    );
    const dwgDatafile = new DataFile({
        itemId: uploadResult.item.itemID,
    });
    try {
        const gpJob = await geoprocessor.submitJob(
            "https://maps.kystverket.cloudgis.no/enterprise/sharing/servers/c0d3e56531604bac9d129f64a90450c5/rest/services/GeoKonvertering/GeokonverteringUtvidet/GPServer/DXF2Features",
            {
                DXF: dwgDatafile,
                Ut_koordinat_system: "EPSG:25833",
                // Ut_fil_navn: fileName,
                // Epost: email,
                // agstoken: "aco-cE928H5HKAOpYH5uxPi8Vp8SdAv6pbWwL5l8kOo."
            },
            undefined,
            { withCredentials: true }
        );
        toast.info(`Starter konvertering av DWG/DXF til Features`, { toastId: "convert-to-features-job" });
        let progress = 0;
        const jobResult = await gpJob.waitForJobCompletion({
            interval: 1000,
            statusCallback: (status: any) => {
                progress =
                    status.jobStatus === "job-succeeded" ? 100 : status.jobStatus === "job-failed" ? 0 : progress + 1;
                if (progress > 99) {
                    progress = 10;
                    toast.info("Prosessering holder fremdeles på ikke vær bekymret.");
                }
                toast.update("convert-to-features-job", {
                    render: `Konverterer... Status: ${status.jobStatus}`,
                    isLoading: true,
                    progress: progress / 100,
                });
            },
        });
        if (jobResult.jobStatus === "job-succeeded") {
            toast.update("convert-to-features-job", {
                render: `Importering av: ${fileName} fullført`,
                type: "success",
                isLoading: false,
                autoClose: 5000,
            });

            const pointFeatures = await jobResult.fetchResultData("Features_Punkt");
            const lineFeatures = await jobResult.fetchResultData("Features_Linje");
            const polygonFeatures = await jobResult.fetchResultData("Features_Polygon");
            addGDOFeaturesToMap(pointFeatures.value, fileName + "_punkt");
            addGDOFeaturesToMap(lineFeatures.value, fileName + "_linje");
            addGDOFeaturesToMap(polygonFeatures.value, fileName + "_polygon");
            return {
                pointFeatures,
                lineFeatures,
                polygonFeatures,
            };
        }
        toast.dismiss("convert-to-features-job");
    } catch (error) {
        toast.dismiss("convert-to-features-job");
        console.error("Error submitting geoprocessing job:", error);
        toast.error("Det oppstod en feil under konvertering av filene.");
    }
};

/**
 *
 * @param dwgFile - A DWG, DXF file or zip collection as a Blob
 * @returns
 */
const convertDWGToLayer = async (dwgFile: Blob, fileName: string, inputSR?: number) => {
    let cleanFileName = fileName
        .replaceAll("æ", "ae")
        .replaceAll("ø", "oe")
        .replaceAll("å", "aa")
        .replaceAll("Æ", "Ae")
        .replaceAll("Ø", "Oe")
        .replaceAll("Å", "Aa");
    cleanFileName = cleanFileName.replaceAll(" ", "_");
    cleanFileName = cleanFileName.replaceAll("-", "_");
    cleanFileName = cleanFileName.replaceAll(/[^a-zA-Z0-9.\\-]/g, "_");

    const cleanFileNameWithoutExtension = cleanFileName.replace(/\.[^/.]+$/, "");
    const uploadResult = await uploadFileToGPServer(dwgFile, AppConfig.CadToLayerUploadUrl, cleanFileName);
    const dwgDatafile = new DataFile({
        itemId: uploadResult.item.itemID,
    });

    const inputSr: { latestWkid: number; wkid: number } = { latestWkid: inputSR ?? 25833, wkid: inputSR ?? 25833 };

    try {
        const gpJob = await geoprocessor.submitJob(
            AppConfig.CadToLayerGPServiceUrl,
            {
                cad_file: dwgDatafile,
                name: cleanFileNameWithoutExtension,
                // portal_url: string has default value in GP tool, can be overridden if needed
                // group_name: string has default value in GP tool, can be overridden if needed
                input_sr: inputSr,
            },
            undefined,
            { withCredentials: true }
        );
        toast.info(`Starter konvertering av DWG/DXF til Features`, { toastId: "convert-to-features-job" });
        let progress = 0;
        const jobResult = await gpJob.waitForJobCompletion({
            interval: 1000,
            statusCallback: (status: any) => {
                progress =
                    status.jobStatus === "job-succeeded" ? 100 : status.jobStatus === "job-failed" ? 0 : progress + 1;
                if (progress > 99) {
                    progress = 10;
                    toast.info("Prosessering holder fremdeles på ikke vær bekymret.");
                }
                toast.update("convert-to-features-job", {
                    render: `Konverterer... Status: ${status.jobStatus}`,
                    isLoading: true,
                    progress: progress / 100,
                });
            },
        });
        if (jobResult.jobStatus === "job-succeeded") {
            toast.update("convert-to-features-job", {
                render: `Importering av: ${fileName} fullført`,
                type: "success",
                isLoading: false,
                autoClose: 5000,
            });
        }
        toast.dismiss("convert-to-features-job");
    } catch (error) {
        toast.dismiss("convert-to-features-job");
        console.error("Error submitting geoprocessing job:", error);
        toast.error("Det oppstod en feil under konvertering av filene.");
    }
};

export enum Tool {
    LIGHT_CALCULATOR,
    MEASURE,
    CREATE_OBJECT,
    SECTOR_EDITOR,
    OVERETT_EDITOR,
    FEATURE_EDITOR,
}

export interface LyrxFileWithOptions {
    file: File;
    name: string;
    spatialReferenceWkid: { latestWkid: number; wkid: number };
    cellSize: number;
    decimalSeparator: "Punkt" | "Komma";
}

/**
 * Using a gptool we upload a .lyrx file and add them to depth raster and pointlayer
 * @param files
 */
export const uploadDepthData = async (
    filesAndOptions: LyrxFileWithOptions[]
): Promise<{ successCount: number; totalCount: number }> => {
    const statusPerFile: Record<string, boolean> = {};
    for (const fileWithOptions of filesAndOptions) {
        const success = await uploadLyrxToGPServer(fileWithOptions);
        statusPerFile[fileWithOptions.name] = success;
    }

    let successCount = 0;
    for (const [fileName, success] of Object.entries(statusPerFile)) {
        if (success) {
            toast.success(`Filen ${fileName} er lastet opp og behandlet.`);
            successCount++;
        } else {
            toast.error(`Det oppstod en feil under opplasting eller behandling av filen ${fileName}.`);
        }
    }
    return { successCount, totalCount: filesAndOptions.length };
};

const uploadLyrxToGPServer = async (fileWithOptions: LyrxFileWithOptions) => {
    toast.info(`Starter opplasting av ${fileWithOptions.name}`, {
        toastId: `upload-depth-data-${fileWithOptions.name}`,
        isLoading: true,
    });
    const response = await uploadFileToGPServer(
        fileWithOptions.file,
        AppConfig.RasterUploadGpServiceUrl,
        fileWithOptions.name
    );
    if (!response?.item?.itemID) {
        console.error("Error uploading raster datafile to GP server.");
        toast.dismiss(`upload-depth-data-${fileWithOptions.name}`);
        return false;
    }

    const uploadRasterDatafile = new DataFile({
        itemId: response.item.itemID,
    });

    const filenameWithoutExtension = fileWithOptions.name.replace(/\.[^/.]+$/, "");
    const uploadLyrxUrl = AppConfig.RasterProcessingGpServiceUrl;
    if (!uploadLyrxUrl) {
        console.error("Depth data service URL is not configured(RasterProcessingGpServiceUrl).");
        toast.dismiss(`upload-depth-data-${fileWithOptions.name}`);
        return false;
    }
    try {
        const gpJob = await geoprocessor.submitJob(uploadLyrxUrl, {
            ascii_file: uploadRasterDatafile,
            name: filenameWithoutExtension,
            spatial_reference: fileWithOptions.spatialReferenceWkid,
            cell_size: { distance: fileWithOptions.cellSize, units: "esriCentimeters" },
            decimal_separator: fileWithOptions.decimalSeparator,
        });
        const progressMessages: string[] = [];
        let checksCounter = 0;
        const jobResult = await gpJob.waitForJobCompletion({
            interval: 1000,
            statusCallback: (status: any) => {
                checksCounter++;
                if (checksCounter % 30 === 0) {
                    toast.info("Prosessering holder fremdeles på ikke vær bekymret.");
                }
                if (status?.progress?.message) {
                    if (!progressMessages.includes(status.progress.message)) {
                        progressMessages.push(status.progress.message);
                    }
                }
                const stepInfo = progressMessages.length > 0 ? ` Prosesserer steg: ${progressMessages.length}` : "";
                toast.update(`upload-depth-data-${fileWithOptions.name}`, {
                    render: `Laster opp... Status: ${status.jobStatus}.${stepInfo}\n Du kan lukke panelet, opplastingen vil fortsette i bakgrunnen.`,
                    isLoading: true,
                });
            },
        });
        if (jobResult.jobStatus === "job-succeeded") {
            toast.update(`upload-depth-data-${fileWithOptions.name}`, {
                render: "Opplasting fullført!",
                type: "success",
                isLoading: false,
                autoClose: 5000,
            });
            return true;
        }
    } catch (error) {
        if ((error as any).messages && Array.isArray((error as any)?.messages)) {
            for (const message of (error as any).messages) {
                if (
                    message?.description?.includes("Rasternavn eksisterer allerede. Fjern raster eller velg nytt navn.")
                ) {
                    toast.error(`Det finnes allerede et raster med samme navn: ${filenameWithoutExtension}`, {
                        autoClose: false,
                    });
                    break;
                }
            }
        }
        toast.dismiss(`upload-depth-data-${fileWithOptions.name}`);
        return false;
    }
    toast.dismiss(`upload-depth-data-${fileWithOptions.name}`);
    return false;
};

const flytendeTildeltSymbolOuter = new SimpleFillSymbol({
    color: [255, 192, 203, 0.5],
    outline: null,
});

const flytendeTildeltSymbolInner = new SimpleFillSymbol({
    color: [0, 255, 0],
    outline: null,
});

export const setupFlytendemerkeTildeltCircles = (
    layerView: FeatureLayerView,
    view: MapView,
    graphicsLayer: GraphicsLayer
) => {
    reactiveUtils.watch(
        () => layerView.layer.visible,
        () => {
            graphicsLayer.visible = layerView.layer.visible;
        }
    );

    reactiveUtils.watch(
        () => layerView.updating,
        () => {
            if (!layerView.updating) {
                layerView
                    .queryFeatures({
                        geometry: view.extent,
                        spatialRelationship: "intersects",
                        returnGeometry: true,
                        outFields: ["*"],
                    })
                    .then((result) => {
                        graphicsLayer.removeAll();

                        const circleGraphics = result.features.flatMap((pointFeature) => {
                            const outerCircle = new Circle({
                                center: pointFeature.geometry as Point,
                                radius: 10,
                                radiusUnit: "meters",
                            });

                            const outerGraphic = new Graphic({
                                geometry: new Polygon({
                                    rings: outerCircle.rings,
                                    spatialReference: outerCircle.spatialReference,
                                }),
                                attributes: pointFeature.attributes,
                                symbol: flytendeTildeltSymbolOuter,
                            });

                            const innerCircle = new Circle({
                                center: pointFeature.geometry as Point,
                                radius: 0.3,
                                radiusUnit: "meters",
                            });

                            const innerGraphic = new Graphic({
                                geometry: new Polygon({
                                    rings: innerCircle.rings,
                                    spatialReference: innerCircle.spatialReference,
                                }),
                                attributes: pointFeature.attributes,
                                symbol: flytendeTildeltSymbolInner,
                            });

                            return [outerGraphic, innerGraphic];
                        });

                        graphicsLayer.addMany(circleGraphics);
                    });
            }
        }
    );
};

export const goToLayerInMap = (item: PortalItem, addLayerToMap: (item: PortalItem) => void) => {
    console.log("Going to layer in map for item: ", item);
    const mapView = getMapView();
    if (!mapView?.map) {
        console.error("MapView not found");
        return;
    }
    addLayerToMap(item);
    const itemExtent = item.extent;
    if (itemExtent) {
        mapView.goTo(itemExtent, { duration: 1000, easing: "ease-in-out", animate: true }).catch((error) => {
            if (error.name != "AbortError") {
                console.error("Error going to layer in map: ", error);
            }
        });
    } else {
        console.error("Item does not have an extent: ", item);
    }
};
